import React, { useCallback, useEffect, useMemo } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { buildUpdateFormatTemplate } from '../../../lib/services/format-template.service';
import { buildAgeGroupBucketOptions, buildStageBlueprintsFromStages, buildStagesFromBlueprints } from '../../../lib/helpers/format-template.helpers';
import { usePendingSubmits } from '../../_core/Formik/use-pending-submits';
import { useStateWithRef } from '../../_core/Formik/use-state-with-ref';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { messages } from '../format-template.messages';
import { useFormatTemplateStages } from '../use-format-template-stages';
import { useFormatTemplateDiagramUpload } from '../use-format-template-diagram-upload';
import { EditFormatTemplateModal } from './edit-format-template-modal.component';
import { IEditFormatTemplateFields } from './edit-format-template-modal.types';
import { validationSchema } from './edit-format-template-modal.validation';
import { KNOWN_AGE_GROUP_BUCKETS } from '../format-template.constants';

interface IEditFormatTemplateModalContainerProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  template: IFormatTemplate | null;
}

const toInitialValues = (template: IFormatTemplate): IEditFormatTemplateFields => ({
  name: template.name,
  ageGroupBuckets: template.ageGroupBuckets ?? [],
  minPlayers: template.minPlayers != null ? String(template.minPlayers) : '',
  maxPlayers: template.maxPlayers != null ? String(template.maxPlayers) : '',
  teamOutcomes: (template.teamOutcomes ?? []).join(', '),
});

export const EditFormatTemplateModalContainer: React.FC<IEditFormatTemplateModalContainerProps> = ({
  open,
  onClose,
  onSaved,
  template,
}) => {
  const { updateFormatTemplate } = buildUpdateFormatTemplate();
  const { drillOptions, practicePlanOptions, positionOptions, loading } = useFormatTemplateStages(template, open);
  const { documentDiagram, uploadingKey, uploadError, selectDiagram, resetDiagrams } =
    useFormatTemplateDiagramUpload(template);

  // Editable copy of the template's stages (config-editable, so isCompleted:false). Reseeded on
  // open; the ref half lets submit read flushed stage edits synchronously (see useStateWithRef).
  const [editStages, setEditStages, editStagesRef] = useStateWithRef<IAssessmentStage[]>([]);
  const { register: registerStageSubmit, flush: flushStageSubmits, clear: clearStageSubmits } = usePendingSubmits();
  useEffect(() => {
    setEditStages(buildStagesFromBlueprints(template?.stageBlueprints ?? [], false));
    clearStageSubmits();
    resetDiagrams();
  }, [template, open, setEditStages, clearStageSubmits, resetDiagrams]);

  const handleUpdateStage = useCallback((id: string, changes: { config?: object; title?: string }) => {
    setEditStages(prev => prev.map(s => (s._id === id ? { ...s, ...changes } : s)));
  }, [setEditStages]);

  const bucketOptions = useMemo(
    () => buildAgeGroupBucketOptions(KNOWN_AGE_GROUP_BUCKETS, template?.ageGroupBuckets ?? []),
    [template],
  );

  const handleSubmit = useCallback(async (
    values: IEditFormatTemplateFields,
    { setSubmitting, setStatus }: FormikHelpers<IEditFormatTemplateFields>,
  ) => {
    if (!template) return;
    setStatus(undefined);

    try {
      // Flush dirty stage forms into the ref before reading it (see usePendingSubmits).
      await flushStageSubmits();

      await updateFormatTemplate(template._id, {
        // The server rejects a rename on a CATALOG family (seed-owned name). Routing keeps this
        // editor CUSTOM-only; omitting name keeps the payload legal even if that ever regresses.
        ...(template.source !== 'CATALOG' && { name: values.name.trim() }),
        ageGroupBuckets: values.ageGroupBuckets,
        minPlayers: values.minPlayers === '' ? undefined : Number(values.minPlayers),
        maxPlayers: values.maxPlayers === '' ? undefined : Number(values.maxPlayers),
        teamOutcomes: values.teamOutcomes.split(',').map(t => t.trim()).filter(Boolean),
        stageBlueprints: buildStageBlueprintsFromStages(editStagesRef.current),
        ...(documentDiagram && { imagePath: documentDiagram.imagePath }),
      });
      onSaved();
    } catch (err) {
      console.error('updateFormatTemplate failed:', err);
      setStatus(<IntlMsg {...messages.editError} />);
    } finally {
      setSubmitting(false);
    }
  }, [template, updateFormatTemplate, onSaved, flushStageSubmits, editStagesRef, documentDiagram]);

  if (!template) return null;

  return (
    // key resets the form to the current template each time the modal opens for a new row.
    <Formik<IEditFormatTemplateFields>
      key={`${template._id}:${open ? 'open' : 'closed'}`}
      initialValues={toInitialValues(template)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {form => (
        <EditFormatTemplateModal
          open={open}
          onClose={onClose}
          bucketOptions={bucketOptions}
          form={form}
          stages={editStages}
          updateStage={handleUpdateStage}
          registerStageSubmit={registerStageSubmit}
          drillOptions={drillOptions}
          practicePlanOptions={practicePlanOptions}
          positionOptions={positionOptions}
          stagesLoading={loading}
          currentImageUrl={template.imageUrl}
          pendingDiagramUrl={documentDiagram?.previewUrl}
          uploadingDiagram={!!uploadingKey}
          diagramError={uploadError}
          onSelectDiagram={selectDiagram}
        />
      )}
    </Formik>
  );
};
